import { useState, useCallback, useRef } from 'react'
import { channelsAPI } from '../services/api'
import { createLogger } from '../utils/logger'
import { useAsyncOperation } from './useAsyncOperation'

const logger = createLogger('ChannelList')

/**
 * Deep compare channels to prevent unnecessary re-renders
 * Only compares fields that matter for UI rendering
 */
function channelsChanged(oldChannels, newChannels) {
  if (oldChannels.length !== newChannels.length) return true
  
  for (let i = 0; i < oldChannels.length; i++) {
    const oldCh = oldChannels[i]
    const newCh = newChannels[i]
    
    // Compare key fields that affect UI
    if (oldCh.id !== newCh.id ||
        oldCh.name !== newCh.name ||
        oldCh.unreadCount !== newCh.unreadCount ||
        oldCh.needsReply !== newCh.needsReply ||
        oldCh.lastMessageAt !== newCh.lastMessageAt) {
      return true
    }
  }
  
  return false
}

/**
 * useChannelList - Manages channel fetching and state
 * 
 * Handles channel list management including mock support channel for non-authenticated users,
 * fetching user channels, and auto-selecting first available channel.
 * 
 * @param {boolean} isAuthenticated - Whether user is authenticated
 * @param {boolean} isAdmin - Whether user is admin
 * @param {Object} user - Current user object
 * @param {Function} t - Translation function
 * @param {string} initialChannelId - Initial channel ID to select (optional)
 * @returns {Object} - { channels, setChannels, activeChannel, setActiveChannel, fetchChannels, loading }
 * 
 * @atomic - <300 lines
 * @idempotent - Same inputs produce same channel list
 */
export function useChannelList(isAuthenticated, isAdmin, user, t, initialChannelId = null) {
  const [channels, setChannels] = useState([])
  const [activeChannel, setActiveChannel] = useState(null)
  const { loading, execute } = useAsyncOperation()
  const initialFetchCompleted = useRef(false)
  const activeChannelRef = useRef(activeChannel)
  
  // Keep ref in sync with state
  activeChannelRef.current = activeChannel

  const fetchChannels = useCallback(async (silent = false) => {
    try {
      // For non-authenticated users, show a mock support channel for contact form
      if (!isAuthenticated) {
        const mockChannel = {
          id: 'mock-support',
          name: 'TrckIt Support',
          description: 'Direct communication with the TrckIt support team',
          channelType: 'default',
          unreadCount: 0
        }
        setChannels([mockChannel])
        setActiveChannel(mockChannel)
        return
      }

      const response = await execute(
        () => channelsAPI.getUserChannels(silent ? { silentError: true } : {}),
        { silent: silent }
      )
      const newChannels = response.data.channels || []
      
      // Only update state if channels actually changed (prevents unnecessary re-renders)
      setChannels(prev => {
        if (channelsChanged(prev, newChannels)) {
          return newChannels
        }
        return prev // Return same reference to preserve memoization
      })
      
      // Only auto-select first channel on initial fetch when there's no active channel
      // This prevents re-selecting when channels are refreshed or when a specific channel was already selected
      // Note: We check initialFetchCompleted to ensure this only runs on first load
      if (newChannels.length > 0 && !activeChannelRef.current && !initialChannelId && !initialFetchCompleted.current) {
        // Always prioritize entity channels (My Channels) first for all users
        // This ensures users land in their favorited entity channels
        const channelToSelect = newChannels.find(ch => ch.channelType === 'entity') || newChannels[0]
        
        setActiveChannel(channelToSelect)
      }
      
      // Mark initial fetch as completed after auto-selection logic
      initialFetchCompleted.current = true
      
      return newChannels
    } catch (error) {
      logger.error('Failed to fetch channels', error)
      throw error
    }
  }, [isAuthenticated, execute, initialChannelId, isAdmin])

  return {
    channels,
    setChannels,
    activeChannel,
    setActiveChannel,
    fetchChannels,
    loading,
    initialFetchCompleted
  }
}
